/**
 * KRINOS TERMS OF SERVICE AND END USER LICENSE AGREEMENT
 * Version 1.2
 * Effective Date: October 23, 2025
 *
 * IMPORTANT LEGAL NOTICE:
 * This is a legally binding agreement. By accepting these terms, you are binding
 * your organization to these terms and waiving certain legal rights, including
 * the right to jury trial and class action lawsuits.
 */

export const TERMS_VERSION = '1.2';
export const EFFECTIVE_DATE = 'October 23, 2025';

export const TERMS_OF_SERVICE = `
# KRINOS TERMS OF SERVICE AND END USER LICENSE AGREEMENT

**Version ${TERMS_VERSION}**
**Effective Date: ${EFFECTIVE_DATE}**

---

## IMPORTANT NOTICE - PLEASE READ CAREFULLY

**THIS IS A LEGALLY BINDING AGREEMENT.** By accepting these Terms of Service ("Terms"), you are entering into a binding contract on behalf of yourself and your organization. You represent and warrant that you have the authority to bind your organization to these Terms.

**YOU ARE WAIVING IMPORTANT LEGAL RIGHTS** including the right to jury trial and the right to participate in class action lawsuits. This agreement requires disputes to be resolved through mediation and arbitration in Guilford County, North Carolina.

---

## 1. ACCEPTANCE OF TERMS AND ORGANIZATIONAL BINDING

### 1.1 Authority to Bind Organization
By accepting these Terms, you ("Signatory") represent, warrant, and covenant that:

(a) You have the full legal authority to bind the organization, company, or entity ("Organization") identified in the signup process to these Terms;

(b) You are at least 18 years of age and have the legal capacity to enter into binding contracts;

(c) The Organization shall be fully bound by these Terms as if the Organization itself had directly accepted them;

(d) All references to "you," "your," "User," or "Customer" in these Terms shall mean both the Signatory and the Organization, jointly and severally.

### 1.2 Scope of Agreement
These Terms govern your use of the Krinos HVAC assessment software platform ("Software" or "Platform"), including all features, services, content, and functionality provided by Krinos, LLC ("Krinos," "we," "us," or "our"), and any of our affiliates, subsidiaries, employees, officers, directors, contractors, partners, or related entities.

### 1.3 Electronic Signature and Record Retention
**By clicking "I Accept" or otherwise indicating your agreement to these Terms, you:**

(a) **Consent to transact business electronically** and to electronic signatures, contracts, and records under the Electronic Signatures in Global and National Commerce Act (E-SIGN Act, 15 U.S.C. § 7001 et seq.) and the Uniform Electronic Transactions Act (UETA);

(b) **Agree to receive all communications electronically**, including these Terms, amendments, notices, disclosures, privacy policies, and all other documents and communications that we are legally required or choose to provide to you in connection with the Software;

(c) **Acknowledge and consent that Krinos will permanently record your acceptance** of these Terms with the following information for legal verification, evidentiary purposes, and compliance with our record retention obligations:
   - Your IP address at the exact time of acceptance
   - Timestamp of acceptance in Coordinated Universal Time (UTC)
   - Version number of the Terms you accepted
   - User agent information (browser type, version, operating system, and device type)
   - Your identifying information (full name, email address, company name, and job title)
   - Your responses to all acknowledgment checkboxes and acceptance confirmations
   - Cryptographic hash of your acceptance record to ensure tamper-proof integrity

(d) **Understand and agree that access to account creation, billing setup, and use of the Software is impossible without your acceptance** of these Terms, and that such acceptance is a mandatory prerequisite to accessing or using any portion of the Software;

(e) **May request a copy** of these Terms or your complete acceptance record (including all metadata and verification information) at any time by contacting support@krinos.com. We will provide such records in PDF format within five (5) business days;

(f) **Confirm that you have the necessary hardware and software** to access, view, and retain electronic records, including: (i) a computer or mobile device with Internet access, (ii) a current web browser with cookies enabled, (iii) email access, and (iv) sufficient storage capacity or a PDF reader to save or print documents;

(g) **May withdraw your consent** to receive communications electronically only by canceling your account, as electronic communications are integral to the Software's operation and cannot be disabled while maintaining an active account.

**Your electronic acceptance has the same legal force and effect as a handwritten signature.**

---

## 2. MANDATORY CONVEYANCE AND DOWNSTREAM USER OBLIGATIONS

### 2.1 Customer Responsibility for Downstream Users
**You are responsible for ensuring** that all downstream users receive and agree to the applicable disclaimers and limitations as a condition of accessing Software outputs. This responsibility extends to:

(a) All employees, contractors, agents, field staff, technicians, or any other personnel of your Organization who will use or access the Software;

(b) All third parties who may receive, view, or rely upon assessments, reports, calculations, or data generated by the Software through your Organization;

(c) All customers, clients, or end users to whom you provide services utilizing data or outputs from the Software.

### 2.2 Failure to Convey
You acknowledge that:

(a) **Failure to convey these terms to downstream users does not relieve your Organization of liability** for their actions;

(b) Your Organization remains fully responsible for all uses of the Software under your account;

(c) You agree to indemnify and hold harmless Krinos for any claims arising from your failure to properly convey these terms.

### 2.3 Chain of Responsibility
Each downstream user must be informed of and must acknowledge:
- The limitations and disclaimers contained herein
- The requirement to independently verify all calculations and data
- The beta nature of the Software
- The prohibition against relying solely on Software outputs for professional decisions

---

## 3. BETA SOFTWARE AND "AS-IS" WARRANTY DISCLAIMER

### 3.1 Beta Status Acknowledgment
You expressly acknowledge and agree that:

(a) The Software is currently in **BETA STATUS** and is under active development;

(b) Beta software may contain bugs, errors, inaccuracies, defects, or incomplete features;

(c) Data outputs, calculations, recommendations, and assessments **MAY CONTAIN ERRORS** and should never be relied upon as the sole basis for professional decisions;

(d) Features may change, be removed, or become unavailable without notice.

### 3.2 "AS-IS" and "AS-AVAILABLE" Provision
**THE SOFTWARE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND.**

Krinos expressly disclaims all warranties, whether express, implied, statutory, or otherwise, including but not limited to:

- **Implied warranties of merchantability**
- **Implied warranties of fitness for a particular purpose**
- **Implied warranties of non-infringement**
- **Warranties of accuracy, completeness, or reliability**
- **Warranties of uninterrupted or error-free operation**
- **Warranties that defects will be corrected**
- **Warranties regarding the results obtained from use of the Software**

No oral or written information or advice given by Krinos, its employees, or representatives shall create a warranty.

---

## 4. DATA ACCURACY AND CALCULATION DISCLAIMERS

### 4.1 Scientific Data Not Guaranteed
You acknowledge and agree that:

(a) **All data, calculations, estimates, and recommendations provided by the Software are scientific in nature but ARE NOT GUARANTEED TO BE ACCURATE;**

(b) Data is based on algorithms, models, assumptions, and third-party information that may be incomplete, outdated, or incorrect;

(c) Climate data, energy rates, equipment efficiency ratings, and other technical parameters are subject to change and regional variation;

(d) The Software uses generalized models that may not account for all site-specific conditions, equipment variations, or environmental factors.

### 4.2 User-Entered Data Disclaimer
**CRITICAL NOTICE:** The Software relies heavily on data input by field staff, technicians, and users ("User-Entered Data"). You acknowledge that:

(a) **User-Entered Data may be inaccurate, incomplete, or erroneous;**

(b) The quality of Software outputs is directly dependent on the accuracy of User-Entered Data;

(c) Krinos has no ability to verify or validate User-Entered Data;

(d) Krinos assumes no responsibility for errors, omissions, or inaccuracies in User-Entered Data;

(e) "Garbage in, garbage out" principle applies—inaccurate inputs will produce inaccurate outputs.

### 4.3 Mandatory Independent Verification
**YOU ARE STRONGLY ADVISED AND REQUIRED TO:**

(a) **Independently verify ALL calculations, estimates, and recommendations** produced by the Software;

(b) Have all assessments reviewed by qualified HVAC professionals before presenting to customers;

(c) Perform manual calculations to confirm critical figures (energy savings, ROI, system sizing, etc.);

(d) Never rely solely on Software outputs for system design, equipment selection, or customer proposals;

(e) Apply professional judgment and expertise to all Software-generated recommendations.

### 4.4 No Substitute for Professional Judgment
The Software is a **TOOL TO ASSIST PROFESSIONALS**, not a replacement for:
- Professional HVAC engineering expertise
- On-site inspections and manual load calculations
- Industry best practices and building codes
- Professional licensing requirements
- Equipment manufacturer specifications

---

## 5. LIMITATION OF LIABILITY AND HOLD HARMLESS

### 5.1 Maximum Liability Cap
**TO THE FULLEST EXTENT PERMITTED BY LAW**, the total aggregate liability of Krinos, its affiliates, subsidiaries, employees, officers, directors, contractors, agents, partners, and any related entities (collectively, "Krinos Parties") for any and all claims arising out of or related to these Terms or your use of the Software **SHALL NOT EXCEED THE TOTAL AMOUNT PAID BY YOU TO KRINOS** in subscription fees during the twelve (12) months immediately preceding the claim.

### 5.2 Exclusion of Consequential Damages
**IN NO EVENT SHALL KRINOS PARTIES BE LIABLE FOR:**

(a) Indirect, incidental, special, consequential, or punitive damages;
(b) Loss of profits, revenue, business, or anticipated savings;
(c) Loss of data or information;
(d) Loss of business opportunity or goodwill;
(e) Costs of procurement of substitute goods or services;
(f) Personal injury or property damage;
(g) Claims by third parties or customers;
(h) Any damages arising from reliance on Software outputs, calculations, or recommendations;

**EVEN IF KRINOS HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.**

### 5.3 Hold Harmless Agreement
You agree to **HOLD HARMLESS** Krinos Parties from and against any and all:

(a) Claims that Software calculations, estimates, or recommendations were inaccurate;
(b) Claims arising from User-Entered Data errors or omissions;
(c) Claims from your customers, clients, or end users;
(d) Claims arising from your failure to independently verify Software outputs;
(e) Claims arising from equipment failures, performance issues, or customer dissatisfaction;
(f) Regulatory violations or code compliance issues;
(g) Professional liability or malpractice claims related to your use of the Software.

### 5.4 Carve-Outs to Liability Limitations
**To the extent not prohibited by applicable law**, the limitations of liability in Sections 5.1, 5.2, and 5.3 **do not apply** to:

(a) **Gross negligence or willful misconduct** by Krinos Parties;

(b) **Fraud or fraudulent misrepresentation** by Krinos Parties;

(c) **Death or personal injury** caused by Krinos's negligence or defective products;

(d) Your **indemnification obligations** under Section 6 (Indemnification);

(e) **Intellectual property infringement claims** made against Krinos that you caused or contributed to;

(f) Krinos's **confidentiality obligations** or data breach caused by Krinos's gross negligence;

(g) Violations of law that **cannot be contractually limited or excluded** under applicable mandatory law;

(h) Any other liability that **cannot be excluded or limited** under applicable law.

**If any court or arbitrator determines** that any limitation in this Section 5 is unenforceable or invalid, the maximum limitation permitted by applicable law shall apply instead. If no limitation is permitted, the liability shall be determined by applicable law.

---

## 6. INDEMNIFICATION

### 6.1 Your Indemnification Obligations
You agree to **INDEMNIFY, DEFEND, AND HOLD HARMLESS** Krinos Parties from and against any and all:

(a) Claims, demands, actions, suits, or proceedings ("Claims");
(b) Liabilities, damages, losses, costs, and expenses (including reasonable attorneys' fees and court costs);

Arising out of or related to:

(i) Your use or misuse of the Software;
(ii) Your violation of these Terms;
(iii) Your violation of any law, regulation, or third-party right;
(iv) Inaccurate or incomplete User-Entered Data;
(v) Your failure to independently verify calculations or recommendations;
(vi) Your failure to convey disclaimers to downstream users;
(vii) Claims by your customers, clients, employees, or third parties;
(viii) Your breach of the representations and warranties in Section 1.1;
(ix) Any negligent or wrongful act or omission by you or your personnel.

### 6.2 Defense and Settlement
Krinos reserves the right, at its own expense, to assume the exclusive defense and control of any matter subject to indemnification by you, and you agree to cooperate fully with Krinos in such defense. You may not settle any claim that affects Krinos without Krinos's prior written consent.

---

## 7. THIRD-PARTY SERVICES AND DATA

### 7.1 Third-Party Dependencies
The Software integrates with or relies upon third-party services and data sources, including but not limited to:

(a) **Stripe** for payment processing and subscription billing;
(b) **Utility rate databases** for electricity, natural gas, and propane pricing;
(c) **Climate data sources** for weather and geographic information;
(d) **Equipment efficiency databases** (SEER, AFUE, HSPF ratings);
(e) **Government rebate and incentive programs** information;
(f) Other third-party APIs, databases, and services.

### 7.2 No Control Over Third-Party Services
You acknowledge that:

(a) Krinos does not control, endorse, or guarantee the accuracy of third-party data;
(b) Third-party services may be unavailable, change, or terminate without notice;
(c) Third-party data may be outdated, inaccurate, or incomplete;
(d) Krinos is not responsible for third-party service failures or data errors.

### 7.3 Payment Processing
All payment transactions are processed by Stripe, Inc. By using the Software, you agree to Stripe's terms of service and privacy policy. Krinos does not store complete credit card information.

---

## 8. SUBSCRIPTION, BILLING, AND TRIAL TERMS

### 8.1 Subscription Fees
Use of the Software requires a paid subscription. Current pricing is available at signup and is subject to change with thirty (30) days' notice.

### 8.2 Seven-Day Free Trial
New accounts receive a seven (7) day free trial. No charges will be made during the trial period. If you do not cancel before the trial expires, you will be automatically charged the subscription fee.

### 8.3 Automatic Renewal
Subscriptions automatically renew on a monthly basis unless cancelled. You are responsible for cancelling your subscription through your account settings or Stripe billing portal.

### 8.4 Refund Policy
**Subscription fees are non-refundable** except as required by law or at Krinos's sole discretion.

### 8.5 Usage-Based Billing
Additional charges may apply for usage exceeding your subscription plan limits (e.g., assessments beyond included amount). Current per-unit pricing is displayed in your account settings.

### 8.6 Taxes
**You are responsible for all sales, use, value-added, goods and services, and other taxes and duties** imposed by any governmental authority on your subscription or use of the Software (except taxes on Krinos's net income). If Krinos is required by law to collect or pay any such taxes, the applicable amounts will be invoiced to you and must be paid in addition to the subscription fees.

### 8.7 Late Payment and Account Suspension
If any payment is not received by Krinos within five (5) days of the due date:

(a) **Late fees** of 1.5% per month (18% per annum) or the maximum rate permitted by applicable law, whichever is less, may be assessed on the overdue amount;

(b) **Your account may be immediately suspended** without further notice until all outstanding amounts (including late fees) are paid in full;

(c) **Suspended accounts cannot create new assessments**, access premium features, invite team members, or utilize any non-essential Software functionality. View-only access to historical data may be maintained at Krinos's discretion;

(d) **We may terminate your account** if payment is not received within thirty (30) days of the original due date. Termination does not waive any amounts owed;

(e) **You remain liable for all fees incurred** during the suspension period, and suspension does not extend your billing cycle or entitle you to credits or refunds.

### 8.8 No Partial Period Refunds
**Subscription fees are billed in advance for the entire billing period** (monthly or annual, as applicable). If you cancel your subscription or your account is terminated mid-period, **you will not receive a refund or credit for the unused portion** of the prepaid period. Your access to the Software will continue until the end of the prepaid billing period, at which point your subscription will not renew.

### 8.9 Fee Changes and Notice
We may change subscription fees, usage-based charges, or introduce new fees with **thirty (30) days' advance notice** via email or in-app notification. Fee changes will apply to your next billing cycle after the notice period. **If you do not accept the fee change**, you may cancel your subscription before the new fees take effect, and the cancellation will be governed by Section 8.8 (no refund for the current period).

---

## 9. VENUE, JURISDICTION, AND GOVERNING LAW

### 9.1 Limited Court Jurisdiction
**Court jurisdiction in Guilford County, North Carolina is limited to the following purposes only:**

(a) **Confirming, vacating, or modifying arbitration awards** under the Federal Arbitration Act (9 U.S.C. § 9-11);

(b) **Seeking injunctive or equitable relief** for intellectual property rights or confidentiality matters as provided in Section 10.4;

(c) **Pursuing individual claims in small claims court** within the jurisdictional limits as provided in Section 10.6;

(d) **Enforcing any judgment or order** issued under subsections (a), (b), or (c) above.

**All other disputes** must be resolved through the mediation and arbitration procedures described in Section 10. You consent to personal jurisdiction in Guilford County, North Carolina for the limited purposes stated above.

### 9.2 Governing Law
These Terms shall be governed by and construed in accordance with **the laws of the State of North Carolina**, without regard to its conflict of laws principles. The United Nations Convention on Contracts for the International Sale of Goods shall not apply.

---

## 10. HYBRID DISPUTE RESOLUTION CLAUSE (MEDIATION → ARBITRATION)

### 10.1 Mandatory Mediation
**BEFORE FILING ANY ARBITRATION OR LEGAL PROCEEDING**, the parties agree to attempt in good faith to resolve any dispute through mediation:

(a) Either party may initiate mediation by written notice to the other party;

(b) The parties shall mutually select a mediator within thirty (30) days of the mediation notice;

(c) If the parties cannot agree on a mediator, a mediator shall be appointed by the American Arbitration Association ("AAA");

(d) **Mediation shall take place in Guilford County, North Carolina;**

(e) Each party shall bear its own costs of mediation, and the mediator's fees shall be split equally;

(f) Mediation shall be confidential;

(g) The parties agree to participate in mediation in good faith for at least one full-day session;

(h) If mediation does not resolve the dispute within sixty (60) days of the initial mediation notice, either party may proceed to arbitration.

### 10.2 Binding Arbitration
If mediation fails to resolve the dispute, **the dispute shall be resolved exclusively through binding arbitration:**

(a) Arbitration shall be administered by the **American Arbitration Association (AAA)** under its Commercial Arbitration Rules;

(b) **Arbitration shall take place in Guilford County, North Carolina;**

(c) The arbitration shall be conducted by a single arbitrator mutually agreed upon by the parties, or if the parties cannot agree, appointed by the AAA;

(d) The arbitrator's decision shall be final and binding and may be entered as a judgment in any court of competent jurisdiction;

(e) Each party shall bear its own attorneys' fees and costs unless the arbitrator awards fees to the prevailing party;

(f) The arbitrator shall have authority to award any remedy or relief that a court could order, except that the arbitrator shall not have authority to award punitive damages or any other damages excluded by Section 5.2 of these Terms.

(g) **Federal Arbitration Act:** This arbitration agreement is governed by the **Federal Arbitration Act (9 U.S.C. § 1 et seq.)**, which preempts state arbitration law to the fullest extent permitted by law.

(h) **Delegation Clause:** Any dispute about the validity, enforceability, scope, or arbitrability of this arbitration agreement (including disputes about the class action waiver in Section 10.3) shall be decided by the arbitrator, not a court, except as specifically provided in 9 U.S.C. § 10 (vacating awards for fraud, corruption, or evident partiality).

(i) **Confidentiality:** All arbitration proceedings, evidence, documents, testimony, and awards shall be kept strictly confidential and shall not be disclosed except: (i) as required by law or court order, (ii) to enforce or challenge the award in court under Section 9.1(a), or (iii) with the prior written consent of both parties. The parties and the arbitrator shall execute confidentiality agreements prior to commencing arbitration.

### 10.3 Class Action, Jury Trial, and Representative Action Waivers
**YOU EXPRESSLY WAIVE:**

(a) **Your right to a trial by jury;**

(b) **Your right to participate in class action lawsuits or class-wide arbitration;**

(c) Your right to act as a representative or member of a class or in a private attorney general capacity.

**All disputes must be brought in the parties' individual capacity, not as a plaintiff or class member in any purported class or representative proceeding.**

(d) **Applicability to All Forums:** This class action waiver applies to **both arbitration and any court proceedings**, including small claims court and the limited court proceedings described in Section 9.1. You may not bring any claim as a member of a class or in a representative capacity in any forum.

(e) **No Consolidation or Joinder:** You may not consolidate or join your arbitration with any other person's arbitration, participate in any consolidated or representative arbitration, or have your arbitration heard together with any other arbitration proceeding.

(f) **Severability of Class Waiver:** If a court or arbitrator finds any portion of this class action waiver invalid or unenforceable as to a particular claim or request for relief, that claim or request for relief (and only that claim or request) shall be severed and decided by a court after all other claims and requests for relief are arbitrated. The remainder of the class action waiver and the arbitration agreement shall remain in full force and effect.

### 10.4 Exceptions to Arbitration
Notwithstanding the above, either party may seek injunctive or equitable relief in court to protect intellectual property rights or enforce confidentiality obligations.

### 10.5 Severability of Arbitration Clause
If any portion of this arbitration clause is found to be unenforceable, the remainder shall continue in full force and effect. If the class action waiver is found unenforceable, the entire arbitration clause shall be void, and disputes shall be resolved in the courts of Guilford County, North Carolina.

### 10.6 Small Claims Court Exception
**Either party may bring an individual action in small claims court** in Guilford County, North Carolina, if the claim:

(a) Falls within the jurisdictional limits of small claims court (currently $10,000 in North Carolina);

(b) Is brought in the party's individual capacity, not as a class member, representative, or in any consolidated action;

(c) Remains in small claims court and is not removed, transferred, or appealed to a court of general jurisdiction.

**If any party removes or appeals the action from small claims court,** the matter shall immediately proceed to mediation and arbitration under Sections 10.1 and 10.2. The small claims court exception does not waive the class action waiver in Section 10.3.

---

## 11. INTELLECTUAL PROPERTY AND REVERSE ENGINEERING PROHIBITION

### 11.1 Ownership
The Software, including all code, algorithms, designs, trademarks, logos, content, and intellectual property, is owned by Krinos and is protected by copyright, trademark, patent, trade secret, and other intellectual property laws.

### 11.2 Limited License
Subject to your compliance with these Terms, Krinos grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Software solely for your internal business purposes **as an end user only**. This license does not grant you any rights to the underlying technology, algorithms, or source code.

### 11.3 Proprietary Trade Secrets and Algorithms

**The following components of the Software constitute proprietary trade secrets of Krinos, LLC:**

(a) **Algorithms and Calculation Methods:** All mathematical algorithms, calculation methodologies, formulas, computational logic, and analytical techniques used to generate HVAC assessments, energy savings projections, system efficiency ratings, cost estimates, and recommendations;

(b) **Data Models and Structures:** All proprietary data models, database schemas, data relationships, normalization techniques, and information architectures;

(c) **Business Logic and Rules Engines:** All decision trees, scoring systems, recommendation engines, threshold determinations, and automated assessment logic;

(d) **Proprietary Datasets:** All compiled datasets, efficiency lookup tables, regional adjustment factors, age-based degradation models, and calibration data developed by Krinos;

(e) **Software Architecture:** All system designs, API structures, integration patterns, performance optimizations, and technical implementations;

(f) **Machine Learning Models:** Any artificial intelligence, machine learning algorithms, training data, neural network architectures, or predictive models (current or future).

**These trade secrets are protected under the Defend Trade Secrets Act of 2016 (18 U.S.C. § 1836) and North Carolina Trade Secrets Protection Act (N.C. Gen. Stat. § 66-152 et seq.).**

### 11.4 Reverse Engineering and Competitive Use Prohibition

**You expressly agree NOT to:**

(a) **Access or use the Software for the purpose of developing, creating, designing, or operating a competing product or service** in the HVAC assessment, energy audit, system analysis, or related industries;

(b) **Copy, reproduce, modify, adapt, translate, disassemble, decompile, reverse engineer, or otherwise attempt to derive** the source code, object code, algorithms, data models, calculation methods, business logic, or any other proprietary components of the Software, by any means whatsoever;

(c) **Benchmark, analyze, test, probe, or study the Software** for competitive purposes, including but not limited to: comparing the Software's functionality, performance, or outputs to competing products; analyzing the Software to replicate its features or capabilities; or conducting competitive intelligence gathering;

(d) **Circumvent, bypass, disable, or interfere with** any security measures, access controls, usage limitations, or technological protection mechanisms implemented in the Software;

(e) **Extract, scrape, mine, or collect** data, outputs, calculation results, or other information from the Software using automated tools, bots, scrapers, or other data extraction methods for any purpose other than your legitimate internal business use;

(f) **Use the Software to train, develop, or improve** any artificial intelligence, machine learning model, or competing analytical tool;

(g) **Permit, authorize, or enable any third party** (including employees, contractors, consultants, competitors, or any other person or entity) to engage in any of the activities prohibited in subsections (a) through (f) above;

(h) **Share, disclose, or provide access** to the Software's algorithms, calculation methods, technical specifications, or proprietary functionality with any third party, including but not limited to: competitors, industry analysts, software developers, academic researchers, or any person or entity who might use such information for competitive purposes.

**Any violation of this Section 11.4 constitutes a material breach of these Terms**, misappropriation of trade secrets under applicable law, and may subject you to injunctive relief and monetary damages as described in Section 11.7.

### 11.5 Beta User Confidentiality and Non-Disclosure Obligations

**As a beta/early access user of the Software, you acknowledge and agree to the following confidentiality obligations:**

(a) **Confidential Information:** All aspects of the Software, including but not limited to its functionality, features, user interface, design, performance, algorithms, outputs, documentation, roadmap, and any information regarding bugs, errors, limitations, or future development plans, constitute confidential and proprietary information of Krinos ("Confidential Information");

(b) **Non-Disclosure Obligation:** You agree to **keep all Confidential Information strictly confidential** and not to disclose, reveal, share, publish, demonstrate, or communicate any Confidential Information to any third party without Krinos's prior written consent;

(c) **No Screenshots or Recordings:** You will not take screenshots, screen recordings, photographs, video recordings, or any other visual or audio reproductions of the Software or its outputs for the purpose of sharing with third parties or publishing publicly (screenshots for internal documentation or support requests are permitted);

(d) **No Competitive Use:** You will not use any knowledge, insights, or understanding gained from your use of the beta Software to: (i) develop, design, create, or operate a competing product or service; (ii) advise, assist, or consult with any third party developing a competing product; or (iii) reverse engineer or replicate any features, functionality, or capabilities of the Software;

(e) **No Reproductions or Copies:** You will not copy, reproduce, duplicate, archive, or create derivative works of the Software, its documentation, outputs, or any Confidential Information, except as strictly necessary for your authorized internal business use during the beta period;

(f) **Feedback and Improvements:** Any feedback, suggestions, bug reports, improvement ideas, or recommendations you provide to Krinos regarding the Software ("Feedback") shall become the exclusive property of Krinos. You hereby assign all rights, title, and interest in such Feedback to Krinos, and Krinos may use such Feedback without restriction or compensation to you;

(g) **Duration of Confidentiality:** Your confidentiality obligations under this Section 11.5 shall survive termination of these Terms and shall continue for a period of **five (5) years** from the date of termination or until the information ceases to qualify as a trade secret or confidential information, whichever is longer;

(h) **Exceptions:** Your confidentiality obligations do not apply to information that: (i) is or becomes publicly available through no fault of yours; (ii) was rightfully in your possession prior to disclosure by Krinos; (iii) is rightfully received from a third party without confidentiality restrictions; or (iv) must be disclosed pursuant to a court order or legal requirement (provided you notify Krinos promptly and cooperate in any effort to limit or protect the disclosure).

**Violation of these confidentiality obligations constitutes a material breach of these Terms** and may result in immediate termination of your access, pursuit of injunctive relief, and recovery of monetary damages.

### 11.6 Additional Use Restrictions

You further agree NOT to:

(a) **Rent, lease, sell, sublicense, distribute, or transfer** your rights to the Software or access credentials to any third party;

(b) **Remove, alter, obscure, or modify** any copyright notices, trademarks, proprietary legends, disclaimers, or other proprietary markings on or in the Software;

(c) **Frame, mirror, or replicate** any portion of the Software on any other server or device;

(d) **Interfere with or disrupt** the integrity, performance, or operation of the Software or the data contained therein;

(e) **Attempt to gain unauthorized access** to any systems, networks, or data associated with the Software;

(f) **Use the Software in violation of any applicable law**, including but not limited to export control laws, privacy laws, consumer protection laws, or unfair competition laws;

(g) **Use the Software to infringe or misappropriate** any third party's intellectual property rights, trade secrets, confidential information, or proprietary rights.

### 11.7 Injunctive Relief and Remedies

**You acknowledge and agree that:**

(a) **Irreparable Harm:** Any violation of Sections 11.3, 11.4, 11.5, or 11.6 will cause **immediate and irreparable harm** to Krinos for which monetary damages would be inadequate, insufficient, and difficult to calculate;

(b) **Entitlement to Injunctive Relief:** In the event of any actual or threatened breach of these intellectual property, confidentiality, or non-compete provisions, **Krinos shall be entitled to seek immediate injunctive relief and equitable remedies** (including temporary restraining orders, preliminary injunctions, and permanent injunctions) **without the requirement to post bond** and without the need to prove actual damages;

(c) **No Arbitration Requirement for IP Claims:** Notwithstanding the arbitration provisions in Section 10, **Krinos may seek injunctive relief in any court of competent jurisdiction** (including but not limited to courts in Guilford County, North Carolina) to prevent or remedy violations of this Section 11, and you consent to personal jurisdiction for such proceedings;

(d) **Cumulative Remedies:** Krinos's right to seek injunctive relief is in addition to, and not in lieu of, any other remedies available at law or in equity, including but not limited to: (i) monetary damages (including consequential damages), (ii) recovery of Krinos's attorneys' fees and court costs, (iii) disgorgement of profits or unjust enrichment, and (iv) termination of your account and access;

(e) **No Limitation of Liability for IP Violations:** The limitations of liability set forth in Section 5 **DO NOT APPLY** to violations of this Section 11. You agree to be fully liable for all direct, indirect, incidental, consequential, exemplary, and punitive damages arising from your breach of these intellectual property, confidentiality, or competitive use restrictions;

(f) **Burden of Proof:** You acknowledge that proving the misappropriation of trade secrets or competitive use is inherently difficult. Therefore, if Krinos presents credible evidence that you have violated this Section 11, **the burden shifts to you** to prove that you have not misappropriated trade secrets or engaged in prohibited competitive use;

(g) **Third-Party Liability:** You shall be liable for any violations of this Section 11 by your employees, contractors, agents, affiliates, or any third parties to whom you grant access, and you agree to indemnify Krinos for all damages arising from such violations.

### 11.8 Third-Party Open Source Components

The Software may incorporate certain open-source software components that are subject to their own license terms. Such third-party licenses do not grant you any rights to Krinos's proprietary components, algorithms, or trade secrets described in this Section 11.

---

## 12. DATA AND PRIVACY

### 12.1 Your Data
You retain ownership of all User-Entered Data, customer information, and assessment results ("Your Data"). You grant Krinos a license to use Your Data solely to provide the Software services.

### 12.2 Privacy Policy
Collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference.

### 12.3 Data Security
While we implement reasonable security measures, **we cannot guarantee that Your Data will be completely secure** from unauthorized access, loss, or breach. You are responsible for maintaining backups of critical data.

### 12.4 Aggregate Data
Krinos may use anonymized, aggregated data for analytics, research, and service improvement purposes.

### 12.5 Regulated Data and Compliance Limitations

**No Protected Health Information (PHI) or HIPAA Data:**

(a) The Software is **not designed, intended, or certified** for use with Protected Health Information (PHI) as defined under the Health Insurance Portability and Accountability Act (HIPAA);

(b) You **must not upload, input, store, or process** any PHI, medical records, health information, patient data, or any information subject to HIPAA through the Software;

(c) **Krinos is not a HIPAA Business Associate** and will not sign a Business Associate Agreement (BAA) for use of this Software. Any request for HIPAA compliance will be declined;

(d) **You are solely responsible for any HIPAA violations** resulting from your improper use of the Software to process PHI. You agree to indemnify Krinos for all claims, fines, and penalties arising from such violations.

**No Highly Regulated or Sensitive Data:**

(e) The Software is not designed or certified for industries requiring specialized data protection frameworks, including but not limited to: healthcare (HIPAA), financial services (GLBA, PCI-DSS), legal (attorney-client privilege), or government/defense (ITAR, FedRAMP);

(f) You **must not process** through the Software: credit card information (except via Stripe's secure payment forms), social security numbers, financial account numbers, government-issued identification numbers, biometric data, genetic information, or other highly sensitive personal information;

(g) **Processing sensitive data without a Data Processing Agreement (DPA) is a material breach** of these Terms and grounds for immediate termination.

**Data Processing Agreements:**

(h) If your use of the Software requires processing of data subject to GDPR, CCPA, or other privacy regulations, or if you require specialized data protection obligations, you **must contact legal@krinos.com to execute a Data Processing Agreement (DPA)** before using the Software;

(i) **Use of the Software for regulated data without a signed DPA** is strictly prohibited and constitutes a material breach of these Terms, entitling Krinos to immediate termination of your account and pursuit of all legal remedies;

(j) Standard DPA terms are available upon request. Custom DPA terms may require additional fees and legal review.

**Your Compliance Obligations:**

(k) **You are solely responsible** for ensuring your use of the Software complies with all applicable laws, regulations, and industry standards in your jurisdiction, including but not limited to data protection, privacy, consumer protection, and professional licensing requirements;

(l) You represent and warrant that you will not use the Software in any manner that violates applicable law or exposes Krinos to regulatory liability.

---

## 13. TERM AND TERMINATION

### 13.1 Term
These Terms commence upon your acceptance and continue until terminated.

### 13.2 Termination by You
You may cancel your subscription at any time through your account settings. Cancellation takes effect at the end of the current billing period.

### 13.3 Termination by Krinos
Krinos may suspend or terminate your access immediately if:
(a) You violate these Terms;
(b) Your payment fails or account becomes past due;
(c) You engage in fraudulent or illegal activity;
(d) Continuation of service would violate applicable law.

### 13.4 Effect of Termination
Upon termination:
(a) Your right to use the Software immediately ceases;
(b) You remain liable for all fees incurred prior to termination;
(c) Sections 2, 4, 5, 6, 9, 10, 11, and 15 survive termination.

---

## 14. MODIFICATIONS TO TERMS

### 14.1 Right to Modify
Krinos reserves the right to modify these Terms at any time. We will provide notice of material changes via email or in-app notification at least thirty (30) days before the effective date.

### 14.2 Acceptance of Changes
Your continued use of the Software after the effective date constitutes acceptance of the modified Terms. If you do not agree to the changes, you must cancel your subscription.

### 14.3 Arbitration Clause Modifications and Opt-Out Right
**Special rules apply to changes to the arbitration clause (Section 10):**

(a) **Prospective Application Only:** Changes to the arbitration clause apply only to disputes or claims arising **after** the effective date of the modification. Disputes arising before the modification date remain subject to the arbitration clause in effect when the dispute arose.

(b) **30-Day Opt-Out Right:** You have the right to **opt out of any changes to the arbitration clause** (but not other Terms modifications) by sending written notice to legal@krinos.com within thirty (30) days of receiving notification of the arbitration clause change. Your opt-out notice must include your name, email address, company name, and a clear statement that you are opting out of the arbitration clause modifications.

(c) **Effect of Opt-Out:** If you timely opt out of arbitration clause changes, the **previous version of the arbitration clause** will continue to apply to you for all disputes, but all other Terms modifications will still take effect as described in Section 14.2.

(d) **No Retroactive Effect:** Opting out of arbitration clause changes does not affect (i) any arbitration already initiated, (ii) any waiver of claims you previously made, or (iii) the current arbitration clause (it only preserves the current clause against future changes).

(e) **Failure to Opt Out:** If you do not opt out within the 30-day period, you will be deemed to have accepted the arbitration clause modifications.

---

## 15. GENERAL PROVISIONS

### 15.1 Entire Agreement
These Terms, together with our Privacy Policy, constitute the entire agreement between you and Krinos regarding the Software and supersede all prior agreements and understandings.

### 15.2 Severability
If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect, and the invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable.

### 15.3 No Waiver
Krinos's failure to enforce any provision of these Terms does not constitute a waiver of that provision or any other provision.

### 15.4 Assignment
You may not assign or transfer these Terms or your rights under these Terms without Krinos's prior written consent. Krinos may assign these Terms without restriction.

### 15.5 Force Majeure
Krinos shall not be liable for any failure or delay in performance due to causes beyond its reasonable control, including acts of God, natural disasters, war, terrorism, strikes, or Internet service failures.

### 15.6 Independent Contractors
The parties are independent contractors. These Terms do not create any partnership, joint venture, employment, or agency relationship.

### 15.7 Notices
All notices must be in writing and sent to:
- **To Krinos:** legal@krinos.com
- **To You:** The email address associated with your account

### 15.8 Headings
Section headings are for convenience only and do not affect interpretation.

### 15.9 Survival
All provisions that by their nature should survive termination shall survive, including but not limited to indemnification, limitation of liability, dispute resolution, and intellectual property provisions.

---

## 16. CONTACT INFORMATION

**Krinos, LLC**
Legal Department
Email: legal@krinos.com
Mailing Address: [To be provided upon request]

For customer support inquiries (not legal notices), please contact: support@krinos.com

---

## ACKNOWLEDGMENT AND ACCEPTANCE

**BY CLICKING "I ACCEPT" OR USING THE SOFTWARE, YOU ACKNOWLEDGE THAT:**

✓ You have read and understood these Terms in their entirety;

✓ You have the authority to bind your Organization to these Terms;

✓ You agree to convey all disclaimers and waivers to your team members and downstream users;

✓ You understand the Software is in beta and may contain errors;

✓ You agree to independently verify all calculations and recommendations;

✓ You understand you are waiving the right to jury trial and class actions;

✓ You agree to mediation and arbitration in Guilford County, North Carolina;

✓ You agree to hold Krinos Parties harmless from claims arising from your use of the Software;

✓ Your Organization is bound by all terms and conditions contained herein.

---

**Last Updated: ${EFFECTIVE_DATE}**
**Version: ${TERMS_VERSION}**
`;

export default {
  TERMS_VERSION,
  EFFECTIVE_DATE,
  TERMS_OF_SERVICE
};
